import type { ReactNode } from "react";

import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsIcon from "@mui/icons-material/Settings";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StopIcon from "@mui/icons-material/Stop";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";

import { misc, neutral, primary, success } from "app/themes/colors";

import type { ScribeGuideStep } from "./scribe-guide-content";
import { SCRIBE_GUIDE_TOUR_COPY as TOUR } from "./scribe-guide.copy";

/**
 * Small, non-interactive pictures of the real controls, so a first-time user recognises them on
 * the page. Hidden from assistive tech: the card text next to each one says the same thing.
 * Every label matches the app's own wording.
 */

const ringPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.06); opacity: 0.2; }
`;

/** The pulsing ring that marks "this is the button". Still for anyone who prefers less motion. */
export const PULSE_RING_SX = {
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: "-5px",
    borderRadius: "21px",
    border: `3px solid ${misc.purpleText}`,
    opacity: 0.55,
    pointerEvents: "none"
  },
  "@media (prefers-reduced-motion: no-preference)": {
    "&::after": { animation: `${ringPulse} 1.8s ease-in-out infinite` }
  }
} as const;

// The recording widget's own button colour (`widget-button.component.tsx`).
const WIDGET_PILL_BACKGROUND = "hsla(272, 17%, 34%, 0.95)";

function Pill({
  icon,
  label,
  ring,
  background = WIDGET_PILL_BACKGROUND
}: {
  icon?: ReactNode;
  label?: string;
  ring?: boolean;
  background?: string;
}) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 34,
        px: label ? 1.5 : 0,
        width: label ? undefined : 34,
        justifyContent: "center",
        borderRadius: "16px",
        background,
        color: "common.white",
        whiteSpace: "nowrap",
        ...(ring ? PULSE_RING_SX : {})
      }}
    >
      {icon}
      {label && (
        <Typography variant="body1" fontWeight={700} component="span">
          {label}
        </Typography>
      )}
    </Box>
  );
}

const STAGE_SX = {
  height: 190,
  background: misc.lightLavender,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1.5,
  p: 2,
  boxSizing: "border-box"
} as const;

const PAPER_SX = {
  width: "100%",
  maxWidth: 380,
  background: "white",
  borderRadius: "10px",
  boxShadow: "0 2px 10px rgba(74, 19, 80, 0.12)",
  p: 1.75,
  boxSizing: "border-box"
} as const;

function TextBar({ width }: { width: string }) {
  return (
    <Box
      sx={{ height: 8, width, borderRadius: "4px", background: neutral[200] }}
    />
  );
}

function CaptureVisitReplica() {
  return (
    <Box sx={STAGE_SX}>
      <Stack sx={PAPER_SX} spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="body2" fontWeight={700}>
            Chart Notes
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{ color: misc.purpleText }}
            >
              Mic Test
            </Typography>
            <Pill
              icon={<MicIcon fontSize="small" />}
              label="Capture Visit"
              ring
            />
          </Stack>
        </Stack>
        <TextBar width="90%" />
        <TextBar width="70%" />
      </Stack>
    </Box>
  );
}

// The widget's own backgrounds (`medical-scribe-recording-widget.component.tsx`): red while it
// records, purple while it is paused.
const WIDGET_BAR_BACKGROUND = {
  recording: "hsla(3, 100%, 59%, 0.9)",
  paused: "hsla(273, 21%, 54%, 0.9)"
} as const;

function WidgetBar({
  state,
  children
}: {
  state: keyof typeof WIDGET_BAR_BACKGROUND;
  children: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        background: WIDGET_BAR_BACKGROUND[state],
        borderRadius: "8px",
        py: 1,
        px: 1.25,
        color: "common.white",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)"
      }}
    >
      <Typography variant="body1" fontWeight={700}>
        {state === "recording" ? "Recording…" : "Paused…"}
      </Typography>
      {children}
    </Stack>
  );
}

function StopConfirm() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ ...PAPER_SX, maxWidth: 340, py: 1.25 }}
    >
      <Typography variant="body1" sx={{ flex: 1 }}>
        Are you sure you want to stop audio recording?
      </Typography>
      <Typography variant="body1" fontWeight={700} color="primary">
        Cancel
      </Typography>
      <Box
        sx={{
          background: primary[400],
          color: "common.white",
          borderRadius: "4px",
          px: 1.5,
          py: 0.5
        }}
      >
        <Typography variant="body1" fontWeight={700}>
          Yes
        </Typography>
      </Box>
    </Stack>
  );
}

function StopReplica() {
  return (
    <Box sx={STAGE_SX}>
      <WidgetBar state="recording">
        <Pill icon={<PauseIcon fontSize="small" />} />
        <Pill icon={<StopIcon fontSize="small" />} ring />
      </WidgetBar>
      <StopConfirm />
    </Box>
  );
}

/**
 * The tour's fuller picture: the bar while recording, the same bar paused (Resume, and Postpone
 * when the practice has it, exactly as the widget shows them only then), and the stop question.
 */
function StopTourReplica({ showPostpone }: { showPostpone: boolean }) {
  const caption = (text: string) => (
    <Typography
      variant="body1"
      fontWeight={700}
      sx={{ color: neutral[800], justifySelf: "end" }}
    >
      {text}
    </Typography>
  );
  return (
    <Box
      sx={{
        ...STAGE_SX,
        display: "grid",
        gridTemplateColumns: "auto auto",
        justifyContent: "center",
        alignItems: "center",
        columnGap: 2,
        rowGap: 1.5,
        height: "auto",
        minHeight: STAGE_SX.height,
        py: 2.5
      }}
    >
      {caption(TOUR.stopPictureRecording)}
      <Box>
        <WidgetBar state="recording">
          <Pill icon={<PauseIcon fontSize="small" />} />
          <Pill icon={<StopIcon fontSize="small" />} ring />
        </WidgetBar>
      </Box>
      {caption(TOUR.stopPicturePaused)}
      <Box>
        <WidgetBar state="paused">
          <Pill
            icon={<MicIcon fontSize="small" />}
            background={misc.sunoGreen}
          />
          {showPostpone && <Pill label="Postpone" />}
          <Pill icon={<StopIcon fontSize="small" />} />
        </WidgetBar>
      </Box>
      {caption(TOUR.stopPictureStopping)}
      <StopConfirm />
    </Box>
  );
}

function DraftNoteReplica() {
  return (
    <Box sx={STAGE_SX}>
      <Stack sx={PAPER_SX} spacing={0.75}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="body1" fontWeight={700}>
            Hearing Test
          </Typography>
          <OutlinedChip label="Insert Scribe" />
        </Stack>
        <Typography
          variant="body1"
          fontWeight={700}
          sx={{
            background: misc.mutedLavender,
            color: misc.suno,
            borderRadius: "4px",
            px: 1,
            py: 0.5
          }}
        >
          Written by Ambient Scribe using Suno Default SOAP Template
        </Typography>
        <Typography variant="body1">
          <b>Subjective:</b> Trouble hearing in restaurants.
        </Typography>
        <Typography variant="body1">
          <b>Plan:</b> Hearing aid trial, follow-up in 2 weeks.
        </Typography>
      </Stack>
    </Box>
  );
}

function OutlinedChip({ label }: { label: string }) {
  return (
    <Typography
      variant="body1"
      fontWeight={700}
      component="span"
      sx={{
        border: `1px solid ${primary[400]}`,
        color: primary[400],
        borderRadius: "4px",
        px: 1,
        py: 0.25,
        whiteSpace: "nowrap"
      }}
    >
      {label}
    </Typography>
  );
}

/** The recording bar's Mic Test and gear, beside a phone: one microphone check, two places to record. */
function SetupReplica() {
  return (
    <Box sx={{ ...STAGE_SX, flexDirection: "row", gap: 4 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ ...PAPER_SX, width: "auto", maxWidth: "none", py: 1.25 }}
      >
        <Typography
          variant="body1"
          fontWeight={700}
          sx={{
            color: misc.purpleText,
            textDecoration: "underline",
            px: 0.5,
            ...PULSE_RING_SX
          }}
        >
          Mic Test
        </Typography>
        <SettingsIcon sx={{ color: misc.purpleText }} />
        <Pill icon={<MicIcon fontSize="small" />} label="Capture Visit" />
      </Stack>
      <Box
        sx={{
          width: 74,
          height: 132,
          flex: "none",
          borderRadius: "16px",
          border: `3px solid ${misc.suno}`,
          background: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Pill icon={<MicIcon fontSize="small" />} />
      </Box>
    </Box>
  );
}

const BAND_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  p: 1.5,
  borderRadius: "8px",
  background: misc.lightLavender,
  color: misc.suno
} as const;

function AfterVisitSummaryReplica() {
  return (
    <Box sx={BAND_SX}>
      <MailOutlineIcon />
      <Typography variant="body2" fontWeight={700} color="text.primary">
        After Visit Summary
      </Typography>
      <Typography
        variant="body1"
        fontWeight={700}
        sx={{
          ml: "auto",
          background: success[100],
          color: success[800],
          borderRadius: "10px",
          px: 1.25,
          py: 0.25
        }}
      >
        Ready
      </Typography>
    </Box>
  );
}

function ReportReplica({ buttonLabel }: { buttonLabel: string }) {
  return (
    <Box sx={{ ...BAND_SX, flexWrap: "wrap" }}>
      <ShowChartIcon />
      <Typography variant="body2" fontWeight={700} color="text.primary">
        Summary
      </Typography>
      <Box sx={{ ml: "auto" }}>
        <OutlinedChip label={buttonLabel} />
      </Box>
    </Box>
  );
}

function TemplatesReplica() {
  const rows = [
    ["Hearing Test", "Hearing Test note"],
    ["Hearing Aid Fitting", "Fitting note"],
    ["Follow-up", "Suno Default SOAP Template"]
  ];
  return (
    <Box sx={{ ...BAND_SX, flexDirection: "column", alignItems: "stretch" }}>
      {rows.map(([type, template]) => (
        <Stack
          key={type}
          direction="row"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="body1" color="text.primary">
            {type}
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {template}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

/** Large stage for the basics (the three "How it works" cards and setup); a band for the rest. */
export default function ScribeGuideReplica({
  step,
  isTour = false
}: {
  step: ScribeGuideStep;
  /** The dialog has room for a fuller picture than the page's cards. */
  isTour?: boolean;
}) {
  const replica = (() => {
    switch (step.id) {
      case "capture":
        return <CaptureVisitReplica />;
      case "stop":
        return isTour ? (
          <StopTourReplica showPostpone={step.showPostpone === true} />
        ) : (
          <StopReplica />
        );
      case "review":
        return <DraftNoteReplica />;
      case "setup":
        return <SetupReplica />;
      case "after_visit_summary":
        return <AfterVisitSummaryReplica />;
      case "report":
        return <ReportReplica buttonLabel={step.buttonLabel ?? ""} />;
      case "templates":
        return <TemplatesReplica />;
    }
  })();
  return (
    <Box aria-hidden="true" data-testid={`scribe-guide-replica-${step.id}`}>
      {replica}
    </Box>
  );
}
